// اجزای مشترک تیکتینگ کارکنان (AP-CHN-02؛ SCR-STAFF-993، 994): کلید Query، برچسب دسته و رنگ وضعیت، و دیالوگ ثبت تیکت (داخلی، از طرف مراجعه‌کننده، از تماس
// یا پیام ورودی نامعلوم — AC-CHN-041.1).
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import {
  unwrap,
  type ChnCreateTicketRequest,
  type ChnTicketCategoryView,
  type ChnTicketDetailView,
  type ChnTicketPriority,
  type ChnTicketSlaState,
  type ChnTicketSource,
  type ChnTicketStatus,
  type PatientSearchItem,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation, type TFunction } from '@phoenix/i18n';
import { FormDialog, type StatusTone } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PatientPicker } from '../patients/PatientPicker';

export const SCR_STAFF_993 = 'SCR-STAFF-993';
export const SCR_STAFF_994 = 'SCR-STAFF-994';
export const SCR_STAFF_995 = 'SCR-STAFF-995';
export const TICKETS_PATH = '/tickets';

export const TICKET_STATUSES: readonly ChnTicketStatus[] = ['New', 'Assigned', 'InProgress', 'WaitingOnRequester', 'Resolved', 'Closed', 'Cancelled'];
export const TICKET_TYPES = ['PatientComplaint', 'PatientRequest', 'Internal'] as const;
export const TICKET_PRIORITIES: readonly ChnTicketPriority[] = ['Low', 'Normal', 'High', 'Urgent'];

export const chnTicketKeys = {
  all: (tenant?: string) => ['chn', tenant, 'tickets'] as const,
  categories: (tenant?: string, includeInactive = false) => ['chn', tenant, 'ticket-categories', includeInactive] as const,
  detail: (tenant?: string, id?: string) => ['chn', tenant, 'tickets', 'detail', id] as const,
  calls: (tenant?: string) => ['chn', tenant, 'calls'] as const,
};

export function ticketPath(id: string): string {
  return `${TICKETS_PATH}/${id}`;
}

/** نام دسته: نام سفارشی یا ترجمه کد دسته پیش‌فرض. */
export function categoryLabel(t: TFunction, code: string, name?: string | null): string {
  return name ?? t(`chn:tickets.categories.${code}`, { defaultValue: code });
}

/** موضوع تیکت؛ تیکت خودکار بازخورد منفی کلید ثابت دارد (C-AGENT-1502). */
export function ticketSubject(t: TFunction, subject: string, source: ChnTicketSource): string {
  return source === 'Feedback' && subject === 'feedback.negative' ? t('chn:tickets.auto.feedback_subject') : subject;
}

export function ticketDescription(t: TFunction, description: string, source: ChnTicketSource): string {
  if (source === 'Feedback' && description.startsWith('nps:')) {
    const score = description.slice(4);
    return score === '-' ? t('chn:tickets.auto.feedback_no_score') : t('chn:tickets.auto.feedback_description', { score });
  }
  return description;
}

export function statusTone(status: ChnTicketStatus): StatusTone {
  switch (status) {
    case 'New':
      return 'pending';
    case 'Assigned':
      return 'booked';
    case 'InProgress':
      return 'in_service';
    case 'WaitingOnRequester':
      return 'confirmed';
    case 'Resolved':
      return 'done';
    case 'Closed':
      return 'inactive';
    default:
      return 'cancelled';
  }
}

export function slaTone(state: ChnTicketSlaState): StatusTone {
  return state === 'Breached' ? 'error' : state === 'Warning' ? 'pending' : state === 'OnTrack' ? 'active' : 'inactive';
}

export function useTicketCategories(includeInactive = false) {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: chnTicketKeys.categories(session?.tenantId, includeInactive),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/ticket-categories', { params: { query: { includeInactive } } })),
  });
}

/** پیش‌فرض‌های دیالوگ ثبت (از تماس، پیام ورودی یا پرونده). */
export interface TicketPrefill {
  source?: ChnTicketSource;
  /** مراجعه‌کننده ثابت (از تماس یا پیام ورودی)؛ تهی ← انتخاب در فرم. */
  patient?: { id: string; displayName: string } | null;
  callId?: string | null;
  sourceRef?: string | null;
  subject?: string;
  categoryId?: string;
}

const STAFF_SOURCES: readonly ChnTicketSource[] = ['Staff', 'Phone', 'InboundMessage'];

export function CreateTicketDialog({ prefill, onClose }: { prefill?: TicketPrefill; onClose: (created?: ChnTicketDetailView) => void }) {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const categories = useTicketCategories();
  const [categoryId, setCategoryId] = useState(prefill?.categoryId ?? '');
  const [subject, setSubject] = useState(prefill?.subject ?? '');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<ChnTicketPriority | ''>('');
  const fixed = prefill?.patient ?? null;
  const [patient, setPatient] = useState<PatientSearchItem | null>(null);
  const [source, setSource] = useState<ChnTicketSource>(prefill?.source ?? 'Staff');
  const create = useMutation({
    mutationFn: () => {
      const body: ChnCreateTicketRequest = {
        categoryId,
        subject: subject.trim(),
        description: description.trim(),
        priority: priority || null,
        branchId: null,
        patientId: fixed?.id ?? patient?.id ?? null,
        source,
        callId: prefill?.callId ?? null,
        sourceRef: prefill?.sourceRef ?? null,
      };
      return unwrap(tenantApi.POST('/api/v1/channels/tickets', { body, headers: { 'Idempotency-Key': idempotency.current() } }));
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: chnTicketKeys.all(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: chnTicketKeys.calls(session?.tenantId) });
      onClose(created);
    },
  });
  const items = (categories.data?.items ?? []) as ChnTicketCategoryView[];
  return (
    <FormDialog
      open
      titleKey="chn:tickets.create.title"
      screenId={SCR_STAFF_994}
      onSubmit={() => create.mutate()}
      onClose={() => onClose()}
      isDirty={Boolean(subject || description || categoryId)}
      isSubmitting={create.isPending}
      submitError={create.error}
      submitKey="chn:tickets.create.submit"
      maxWidth="md"
    >
      <TextField
        select
        required
        label={t('chn:tickets.create.category')}
        value={categoryId}
        onChange={(event) => setCategoryId(event.target.value)}
        slotProps={{ htmlInput: { 'data-testid': 'chn-ticket-category' } }}
      >
        {TICKET_TYPES.flatMap((type) => [
          <MenuItem key={type} disabled value={`type-${type}`}>
            {t(`chn:tickets.type.${type}`)}
          </MenuItem>,
          ...items
            .filter((item) => item.type === type)
            .map((item) => (
              <MenuItem key={item.id} value={item.id} sx={{ pl: 4 }}>
                {categoryLabel(t, item.code, item.name)}
                {item.isClinical ? ` — ${t('chn:tickets.clinical')}` : ''}
              </MenuItem>
            )),
        ])}
      </TextField>
      <TextField required label={t('chn:tickets.create.subject')} value={subject} onChange={(event) => setSubject(event.target.value)} slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'chn-ticket-subject' } }} />
      <TextField required multiline minRows={3} label={t('chn:tickets.create.description')} value={description} onChange={(event) => setDescription(event.target.value)} slotProps={{ htmlInput: { maxLength: 4000, 'data-testid': 'chn-ticket-description' } }} />
      <TextField select label={t('chn:tickets.create.priority')} value={priority} onChange={(event) => setPriority(event.target.value as ChnTicketPriority | '')}>
        <MenuItem value="">{t('chn:tickets.create.priority_default')}</MenuItem>
        {TICKET_PRIORITIES.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`chn:tickets.priority.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField select label={t('chn:tickets.create.source')} value={source} onChange={(event) => setSource(event.target.value as ChnTicketSource)} disabled={Boolean(prefill?.source)}>
        {STAFF_SOURCES.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`chn:tickets.source.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      {fixed ? (
        <TextField label={t('chn:tickets.create.patient')} value={fixed.displayName || t('chn:tickets.create.patient_linked')} disabled />
      ) : (
        <PatientPicker labelKey="chn:tickets.create.patient" value={patient} onChange={setPatient} testId="chn-ticket-patient" />
      )}
    </FormDialog>
  );
}
